// features/project/projectSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FetchProjectParams,
  Project,
  ProjectCreateResponse,
  ProjectDetailsResponse,
  ProjectListResponse,
} from "../types/project";
import {
  createProjectAPI,
  deleteProject,
  detailProject,
  fetchProjectList,
  updateProject,
} from "../services/projectServices";

// create project
export const createProject = createAsyncThunk<
  ProjectCreateResponse, // ✅ Return type
  FormData, // ✅ Argument
  { rejectValue: string } // ✅ Error type
>("project/createProject", async (formData, { rejectWithValue }) => {
  try {
    const response = await createProjectAPI(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error || "Failed to create project");
  }
});

// update Project
export const updateProjectById = createAsyncThunk<
  ProjectCreateResponse,
  { id: number; formData: FormData },
  { rejectValue: string }
>("project/update", async ({ id, formData }, { rejectWithValue }) => {
  try {
    const res = await updateProject(id, formData); // ⬅️ From your service
    return res;
  } catch (err: any) {
    return rejectWithValue(err || "Failed to update project");
  }
});

// Thunk to fetch all projects
export const fetchAllProjects = createAsyncThunk<
  ProjectListResponse,
  FetchProjectParams,
  { rejectValue: string }
>("project/fetchAll", async (params, { rejectWithValue }) => {
  try {
    return await fetchProjectList(params);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch project list");
  }
});

// delete Project
export const deleteProjectById = createAsyncThunk<
  ProjectListResponse,
  number,
  { rejectValue: string }
>("project/delete", async (id, { rejectWithValue }) => {
  try {
    return await deleteProject(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to delete user");
  }
});

// detail by id project
export const detailProjectById = createAsyncThunk<
  ProjectDetailsResponse,
  number,
  { rejectValue: string }
>("project/detail", async (id, { rejectWithValue }) => {
  try {
    return await detailProject(id);
  } catch (err: any) {
    return rejectWithValue(err.message || "Failed to detail project");
  }
});

// Slice State
interface ProjectState {
  loading: boolean;
  error: string | null;
  success: boolean;
  projectList: Project[];
  total: number;
  projectDetail: Project | null; // ✅ Add this line
}

// Initial State
const initialState: ProjectState = {
  loading: false,
  error: null,
  success: false,
  projectList: [],
  total: 0,
  projectDetail: null, // ✅ Add this line
};

// Slice
const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    resetProjectState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.projectList = [];
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // create project
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.projectList = [action.payload.data, ...state.projectList]; // Optionally prepend
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload || "Failed to create project";
      })
      // project list
      .addCase(fetchAllProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projectList = action.payload.data.data;
        state.total = action.payload.data.total;
      })
      .addCase(fetchAllProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch project list";
      })

      // delete Role
      .addCase(deleteProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectById.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        state.projectList = state.projectList.filter(
          (project) => project.id !== deletedId
        );
        state.loading = false;
      })

      .addCase(deleteProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to Delete role";
      })

      // update project
      .addCase(updateProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updated = action.payload.data;
        state.projectList = state.projectList.map((project) =>
          project.id === updated.id ? { ...project, ...updated } : project
        );
      })
      .addCase(updateProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update project";
      })

      // detail project
      .addCase(detailProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(detailProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.projectDetail = action.payload.data; // store project detail
      })
      .addCase(detailProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch project details";
      });
  },
});

export const { resetProjectState } = projectSlice.actions;
export default projectSlice.reducer;
